import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import Publish from "./publish";
import Footer from "../animo-player/footer";
import {
  Flex,
  LeftSection,
  WorkspaceWrapper,
  RightSection,
  HeadingSec,
  Icon,
  Left,
  PlayerSection,
  MobileSection,
  ActionButton,
  RightFlex,
  Hellobar
} from "./publish-component";
import Workspace from "../containers/workspace/workspace";
import { Loader } from "../animo-player/animo-component";
import Spinner, { SPINNER_TYPE } from "../common-components/Spinner";
import PlayerControlPanel from "../animo-player/player-controls";
import ShortsPlayerControlPanel from "../containers/shorts/shorts-player-control";
import Typography from "../common-components/Typography";
import Action from "../common-components/Action";
import { font } from "../constants/font";
import { ENVIRONMENT, STATIC_PATH } from "../constants/config";
import { validateKeydown } from "../helper/keymap";
import ActionTypes from "../constants/action-types";
import { useCustomCallback } from "../helper/hooks/useCustomCallback";
import content from "../constants/content";
import ShortsContextProvider from "../containers/shorts/shortsContextApi";
import useWatermark from "../helper/hooks/useWatermark";
import useUpgrade from "../helper/hooks/useUpgrade";
import FeedbackModal from "./feedback-modal";
import { PLAN } from "../constants";
import TRACKERS from "../constants/trackers";
import trackerEvents from "../helper/trackerEvents";

const PublishContainer = ({ isExport, isPublish, shortId }) => {

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const appReducer = useSelector((state) => state.app);
  const isWorkspaceTextFocus = appReducer.get("isWorkspaceTextFocus");
  const textStatus = appReducer.get("textStatus");
  const initStages = appReducer.get("initStages");
  const animoPlayerId = appReducer.get("animoPlayerId");
  const projectDetails = useSelector((state) => state.projectDetails);
  const isShorts = !!shortId;
  const userDetails = useSelector((state) => state.userDetails);
  const { plan } = useSelector((store) => store.userDetails);
  const sourceType = userDetails?.sourceType;
  const { projectId } = userDetails;
  const { isLiteProceed = 0 } = userDetails;
  const isWaterMark = useWatermark();
  const { showPricingModal } = useUpgrade();
  let shortData;
  if (isShorts) {
    shortData = projectDetails.getIn(["shorts", shortId]);
  }

  const [playerShortcut, setPlayerShortcut] = useState("");
  const [isMobileShow, setIsMobileShow] = useState(false);
  const [hideCloseIcon, setHideCloseIcon] = useState(false);
  const feedbackModal = appReducer.get("feedbackModal");
  const showFeedbackModal = feedbackModal.get("show");

  const handleKeyDown = (e) => {
    setPlayerShortcut((prevState) => {
      const optionArr = validateKeydown(e, isWorkspaceTextFocus, textStatus);
      let tempState = prevState;
      optionArr.forEach(({ component, option }) => {
        if ((component === "playerControl") && ((!isWorkspaceTextFocus && !textStatus.get("isFocused")))) {
          // Prevent the default behaviour on arrow click.
          if (["ArrowLeft", "ArrowRight"].indexOf(e.code) > -1) {
            e.preventDefault();
          }
          let keyoption = option;
          if (keyoption !== "") {
            if (prevState === keyoption) {
              keyoption += "_AGAIN";
            }
            tempState = keyoption;
          }
        }
      });
      return tempState;
    });
  };

  const handleKeyUp = () => {
    if (playerShortcut) {
      setPlayerShortcut("");
    }
  };

  const handleResize = () => {
    dispatch({
      type: ActionTypes.SET_RESIZE,
    });
  };

  const onKeyDown = useCustomCallback({ memoizedCallback: handleKeyDown });
  const onKeyUp = useCustomCallback({ memoizedCallback: handleKeyUp });
  const onResize = useCustomCallback({ memoizedCallback: handleResize });

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.addEventListener("keyup", onKeyUp);
      window.addEventListener("resize", onResize);
    };
  }, [onKeyDown, onKeyUp, onResize]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const exportType = params.get("export_type");
    setHideCloseIcon(!!exportType);
    if (!isMobileShow) {
      // To toggle up the export popup for mobile on page refresh.
      setIsMobileShow(!!exportType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const upgrade = useCallback((e) => {
    e?.preventDefault();
    trackerEvents(TRACKERS.REMOVE_WATERMARK_FROM_EXPORT, {
      projectId,
      ...(shortId ? { shortId } : {}),
    });
    showPricingModal({ type: "watermark", source: "Direct-download" });
  }, [projectId, shortId, showPricingModal]);

  const onAction = useCallback(() => {
    trackerEvents(TRACKERS.EXPORT_TO_EDIT, {
      projectId,
      ...(shortId ? { shortId } : {}),
    });
    history.push(`${ENVIRONMENT === "LOCAL" ? "" : "/editproject"}/${projectId}`);
  }, [history, projectId, shortId]);

  const toggleMobilePopup = () => {
    setIsMobileShow(!isMobileShow);
  }

  let workspaceComp = null;
  if (initStages.get("projectLoaded")) {
    workspaceComp = <Workspace />;
  }

  return (
    <Flex>
      <LeftSection>
        <HeadingSec>
          <Left>
            <Typography
              content={content.VIDEO_IS_READY_FOR_EXPORT}
              color="#32304A"
              font={font.boldSmall}
              display="block"
            />
          </Left>
          <RightFlex>
            {isWaterMark ? <ActionButton
              text={content.REMOVE_WATERMARK}
              className="watermark-btn"
              icon={`${STATIC_PATH}upgrade-stars.svg`}
              onClick={(e) => upgrade(e, isWaterMark)}
            /> : null}
            {!isShorts &&
              <ActionButton
                text={content.EDIT_VIDEO}
                icon={`${STATIC_PATH}edit.svg`}
                className="editpreview-btn"
                onClick={onAction}
              />}
          </RightFlex>
        </HeadingSec>
        <PlayerSection>
          <WorkspaceWrapper id={animoPlayerId}>
            {workspaceComp || (
              <Loader>
                <Spinner
                  type={SPINNER_TYPE.RING_SPINNER}
                  spinnerHeight={"130px"}
                />
              </Loader>
            )}
            {isShorts && shortData && shortData.get("start") !== undefined && shortData.get("end") !== undefined ? (
              <ShortsContextProvider>
                <ShortsPlayerControlPanel
                  shortcutName={playerShortcut}
                  startTime={shortData.get("start")}
                  endTime={shortData.get("end")}
                  isShorts={location.pathname.includes('shorts')}
                />
              </ShortsContextProvider>
            ) : (
              <PlayerControlPanel shortcutName={playerShortcut} />
            )}
          </WorkspaceWrapper>
          {/* is watermark condition to avoid non avatar project */}
          {+isLiteProceed && isWaterMark ?
            <Hellobar>
              <Typography
                content={content.AVAILABLE_CREDITS}
                color={"#313345"}
                font={font.normalMini_14}
                enableTrim={false}
                className="available-credits"
                beforeIcon={`${STATIC_PATH}info-black.svg`}
                display={"block"}
              />
              <Action
                text={content.UPGRADE_NOW}
                width="auto"
                height="32px"
                background="#FA7907"
                hoverColor="#EA6D00"
                className="generate-btn"
                borderRadius={"8px"}
                padding="0px 16px"
                icon={`${STATIC_PATH}white-stars.svg`}
                font={font.mediumBase_21}
                onClick={upgrade}
              />
            </Hellobar> :
            <Typography
              beforeIcon={`${STATIC_PATH}info-outline.svg`}
              content={(plan !== PLAN.LITE && sourceType === 'prompt-to-video') ?
                content.PREVIEW_IN_SD_QUALITY_IN_WATERMARK :
                content.PREVIEW_IN_SD_QUALITY}
              color="#787B80"
              enableTrim={false}
              font={font.normalMicroLarge_18}
            />
          }
          <MobileSection>
            {!isShorts &&
              <ActionButton
                text={content.EDIT_VIDEO}
                icon={`${STATIC_PATH}edit.svg`}
                className="mobEditpreview-btn"
                onClick={onAction}
              />}
            <ActionButton
              text={content.DOWNLOAD}
              icon={`${STATIC_PATH}download-icon2.svg`}
              className="download-btn"
              onClick={toggleMobilePopup}
            />
            {isWaterMark ? <ActionButton
              text={content.REMOVE_WATERMARK}
              className="mobWatermark-btn"
              icon={`${STATIC_PATH}upgrade-stars.svg`}
              onClick={(e) => upgrade(e, isWaterMark)}
            /> : null}
          </MobileSection>
        </PlayerSection>
        <Footer isExport={isExport} />
        <FeedbackModal
          showModal={showFeedbackModal}
        />
      </LeftSection>
      <RightSection
        isMobileShow={isMobileShow}
      >
        {!hideCloseIcon && (
          <Icon
            src={`${STATIC_PATH}close-icon2.svg`}
            className="closeIcon"
            onClick={toggleMobilePopup}
          />
        )}
        <Publish
          isShortsPlayer={!!(isShorts && shortData)}
          shortId={shortId}
          isPublish={isPublish}
        />
      </RightSection>
    </Flex>
  );
};

ActionButton.propTypes = {
  text: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired
};

PublishContainer.propTypes = {
  isExport: PropTypes.bool,
  isPublish: PropTypes.bool,
  shortId: PropTypes.string
};

export default PublishContainer;
